<!--
    The outer wrapper here is NOT a landmark — the plugin loads inside the
    container's `<dialog>` host, which already owns the dialog role. Keeping
    it as a plain `<div>` with `aria-labelledby` lets a screen reader anchor
    to the title through the host without introducing a nested `role="dialog"`.
-->
<div class="evercycle-buyback-dialog-plugin" hive-root aria-labelledby="evercycle-buyback-dialog-title">
    <header class="evercycle-buyback-dialog-plugin__header">
        <hive-label
            id="evercycle-buyback-dialog-title"
            label="{{ programLabel }} for {{ renderInfo().targetName }}"
            size="medium"
            weight="semibold"
        ></hive-label>
    </header>

    <div class="evercycle-buyback-dialog-plugin__body">
        <section class="evercycle-buyback-dialog-plugin__valuation" [attr.data-actionable]="isActionable()">
            @if (isActionable() && renderInfo().valuation; as valuation) {
                <hive-label [label]="valuationCaption" size="medium"></hive-label>
                <!--
                    A paragraph rather than a hive-label: hive-label only offers label
                    sizes and pins its own text colour, and the amount uses the heading
                    size and the accent colour.
                -->
                <p
                    class="evercycle-buyback-dialog-plugin__valuation-amount evercycle-buyback-dialog-plugin__valuation-amount--live"
                >
                    {{ valuation.totalAmount | currency: valuation.currency : 'symbol' : '1.0-0' }}
                </p>
                <hive-label [label]="valuationQualifier" size="smallest"></hive-label>
                @if (validUntilDate(); as deadline) {
                    <hive-label
                        class="evercycle-buyback-dialog-plugin__validity"
                        [label]="'Offer valid until ' + (deadline | date: 'yyyy-MM-dd' : 'UTC')"
                        size="smallest"
                    ></hive-label>
                }
            } @else {
                <hive-label label="Valuation" size="medium"></hive-label>
                <p class="evercycle-buyback-dialog-plugin__valuation-amount">{{ pendingLabel }}</p>
            }
        </section>

        <!--
            Each hive-button wraps a native <button> that handles Enter/Space
            as keyboard clicks natively, so adding a bare `(keydown.enter)`
            here is defensive — it satisfies SonarQube's
            MouseEventWithoutKeyboardEquivalentCheck for the custom-element
            tag and never fires in practice (focus is on the inner <button>).
        -->
        <section class="evercycle-buyback-dialog-plugin__options">
            <hive-button
                class="evercycle-buyback-dialog-plugin__option"
                label="Renew as-is"
                emphasis="secondary"
                (click)="selectOption('RENEW_AS_IS')"
                (keydown.enter)="selectOption('RENEW_AS_IS')"
            ></hive-button>
            <hive-button
                class="evercycle-buyback-dialog-plugin__option"
                label="Renew with Forever upgrade"
                emphasis="secondary"
                (click)="selectOption('RENEW_WITH_FOREVER')"
                (keydown.enter)="selectOption('RENEW_WITH_FOREVER')"
            ></hive-button>
            <hive-button
                class="evercycle-buyback-dialog-plugin__option"
                [label]="programLabel"
                emphasis="primary"
                [disabled]="!isActionable()"
                (click)="selectOption('EVERCYCLE_BUYBACK')"
                (keydown.enter)="selectOption('EVERCYCLE_BUYBACK')"
            ></hive-button>
        </section>

        @if (isActionable()) {
            <section class="evercycle-buyback-dialog-plugin__accept">
                <hive-inline-notification
                    class="evercycle-buyback-dialog-plugin__payment-terms"
                    severity="info"
                    [showIcon]="false"
                >
                    {{ paymentTerms }}
                </hive-inline-notification>
                <hive-checkbox
                    class="evercycle-buyback-dialog-plugin__agreement"
                    [required]="true"
                    [checked]="agreementChecked()"
                    [disabled]="acceptStatus() !== 'idle'"
                    (checkedChange)="toggleAgreement($event)"
                >
                    I have read and agree to the {{ programLabel }} terms and conditions, including data sanitization
                    requirements and shipment deadlines.
                </hive-checkbox>
                <!--
                    `<output>` is the semantic element for a result of a user
                    action and ships `role="status" aria-live="polite"` by
                    default. Using the native tag (instead of a `<div
                    role="status">`) satisfies SonarQube S6819 and still
                    announces the success / pending / permission message
                    asynchronously once `acceptMessage()` becomes non-empty.
                -->
                <output class="evercycle-buyback-dialog-plugin__accept-message">
                    @if (acceptMessage()) {
                        <hive-label [label]="acceptMessage()" size="smallest"></hive-label>
                    }
                </output>
            </section>
        }

        @if (exclusionLines().length > 0) {
            <section class="evercycle-buyback-dialog-plugin__exclusions">
                <hive-label label="Not eligible for buyback:" size="smallest" weight="semibold"></hive-label>
                <ul>
                    @for (line of exclusionLines(); track line.label) {
                        <li>
                            <hive-label label="{{ line.label }} — {{ line.reasonCopy }}" size="smallest"></hive-label>
                        </li>
                    }
                </ul>
            </section>
        }

        <section class="evercycle-buyback-dialog-plugin__docs">
            <a
                class="evercycle-buyback-dialog-plugin__docs-link"
                [href]="renderInfo().documentationUrl"
                target="_blank"
                rel="noopener noreferrer"
                >Learn more about {{ programLabel }}</a
            >
        </section>
    </div>

    <footer class="evercycle-buyback-dialog-plugin__footer">
        <hive-button
            label="Cancel"
            emphasis="secondary"
            (click)="handleActionEvent(RecommendationActionEvent.CANCEL)"
            (keydown.enter)="handleActionEvent(RecommendationActionEvent.CANCEL)"
        ></hive-button>
        @if (isActionable()) {
            <hive-button
                class="evercycle-buyback-dialog-plugin__accept-button"
                label="Accept Valuation"
                emphasis="primary"
                [disabled]="acceptDisabled()"
                (click)="accept()"
                (keydown.enter)="accept()"
            ></hive-button>
        }
    </footer>
</div>
